import {
  Button,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { modalityList } from "../services/modality";
import { useQuery } from "@tanstack/react-query";
import Loading from "./Loading";
import { professorsList } from "../services/professors";

const StudentFilters = ({
  open,
  onClose,
  onFilter,
}: {
  open: boolean;
  onClose: () => void;
  onFilter: (v: {
    financeStatus: string;
    fightId: string;
    graduation: string;
    nextGraduation: string;
    professorId: string;
  }) => void;
}) => {
  const [filters, setFilters] = useState({
    financeStatus: "",
    fightId: "",
    graduation: "",
    nextGraduation: "",
    professorId: "",
  });

  const cleanFilter = () => {
    setFilters({
      financeStatus: "",
      fightId: "",
      graduation: "",
      nextGraduation: "",
      professorId: "",
    });
    onFilter({
      financeStatus: "",
      fightId: "",
      graduation: "",
      nextGraduation: "",
      professorId: "",
    });
  };

  const {
    data: dataModality,
    isLoading: isLoadingModality,
    isFetching: isFetchingModality,
  }: {
    data: Array<{
      id: string;
      name: string;
      description: string;
      createdAt: string | Date;
      status: boolean;
      graduations?: { id: number; belt: string }[];
    }>;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["modalityList"],
    queryFn: async () => await modalityList({}),
    initialData: [],
  });

  const {
    data: dataProfessors,
    isLoading: isLoadingProfessors,
    isFetching: isFetchingProfessors,
  }: {
    data: Array<{
      id: string;
      fullName: string;
      email: string;
      phone: string;
      createdAt: string | Date;
      status: string;
    }>;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["professorsList"],
    queryFn: async () => await professorsList({}),
    initialData: [],
  });

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={() => {
        onFilter(filters);
        onClose();
      }}
    >
      <Loading
        loading={
          isLoadingModality ||
          isFetchingModality ||
          isLoadingProfessors ||
          isFetchingProfessors
        }
      />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={2} padding={1}>
          <Grid2 size={12}>
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h2" fontSize={20} fontWeight={600}>
                Filtrar alunos
              </Typography>
              <IconButton
                onClick={() => {
                  onFilter(filters);
                  onClose();
                }}
              >
                <IconX style={styles.iconStyle} />
              </IconButton>
            </Stack>
          </Grid2>
          <Grid2 size={12}>
            <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
              Modalidades
            </Typography>
            <Select
              sx={{ marginTop: 1 }}
              id="fightId"
              value={filters.fightId}
              fullWidth
            >
              <MenuItem
                value=""
                onClick={() => setFilters({ ...filters, fightId: "" })}
              >
                Limpar
              </MenuItem>
              {dataModality?.map((item) => (
                <MenuItem
                  key={item?.id}
                  value={item?.id}
                  onClick={() => setFilters({ ...filters, fightId: item.id })}
                >
                  {item?.name}
                </MenuItem>
              ))}
            </Select>
          </Grid2>
          <Grid2 size={12}>
            <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
              Status financeiro
            </Typography>
            <Select
              sx={{ marginTop: 1 }}
              id="financeStatus"
              value={filters.financeStatus}
              fullWidth
            >
              <MenuItem
                value=""
                onClick={() => setFilters({ ...filters, financeStatus: "" })}
              >
                Limpar
              </MenuItem>
              <MenuItem
                value="PENDING"
                onClick={() =>
                  setFilters({ ...filters, financeStatus: "PENDING" })
                }
              >
                Pagamentos pendentes
              </MenuItem>
              <MenuItem
                value="WITHOUT_PLAN"
                onClick={() =>
                  setFilters({ ...filters, financeStatus: "WITHOUT_PLAN" })
                }
              >
                Sem plano cadastrado
              </MenuItem>
              <MenuItem
                value="PAID"
                onClick={() =>
                  setFilters({ ...filters, financeStatus: "PAID" })
                }
              >
                Pagamentos em dia
              </MenuItem>
            </Select>
          </Grid2>
          <Grid2 size={12}>
            <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
              Graduação atual
            </Typography>
            <Select
              sx={{ marginTop: 1 }}
              id="graduation"
              value={filters.graduation}
              fullWidth
            >
              <MenuItem
                value=""
                onClick={() => setFilters({ ...filters, graduation: "" })}
              >
                Limpar
              </MenuItem>
              {dataModality?.map((item) =>
                item?.graduations?.map((graduation) => {
                  return (
                    <MenuItem
                      key={item?.id}
                      value={`${item.id},${graduation.id}`}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          graduation: `${item.id},${graduation.id}`,
                        })
                      }
                    >
                      {`${item.name} - ${graduation.belt}`}
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </Grid2>
          <Grid2 size={12}>
            <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
              Próxima graduação
            </Typography>
            <Select
              sx={{ marginTop: 1 }}
              id="nextGraduation"
              value={filters.nextGraduation}
              fullWidth
            >
              <MenuItem
                value=""
                onClick={() => setFilters({ ...filters, nextGraduation: "" })}
              >
                Limpar
              </MenuItem>
              <MenuItem
                value="CLOSE_TO_GRADUATING"
                onClick={() =>
                  setFilters({
                    ...filters,
                    nextGraduation: "CLOSE_TO_GRADUATING",
                  })
                }
              >
                Próximos a graduar
              </MenuItem>
              <MenuItem
                value="READY_TO_GRADUATE"
                onClick={() =>
                  setFilters({
                    ...filters,
                    nextGraduation: "READY_TO_GRADUATE",
                  })
                }
              >
                Prontos para graduar
              </MenuItem>
            </Select>
          </Grid2>
          <Grid2 size={12}>
            <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
              Professor
            </Typography>
            <Select
              sx={{ marginTop: 1 }}
              id="professorId"
              value={filters.professorId}
              fullWidth
              disabled={isLoadingProfessors || isFetchingProfessors}
            >
              <MenuItem
                value=""
                onClick={() => setFilters({ ...filters, professorId: "" })}
              >
                Limpar
              </MenuItem>

              {dataProfessors?.map((prof) => (
                <MenuItem
                  key={prof.id}
                  value={String(prof.id)}
                  onClick={() =>
                    setFilters({
                      ...filters,
                      professorId: String(prof.id),
                    })
                  }
                >
                  {prof.fullName}
                </MenuItem>
              ))}
            </Select>
          </Grid2>
          <Grid2 size={12}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => onFilter(filters)}
            >
              Filtrar
            </Button>
          </Grid2>
          <Grid2 size={12}>
            <Button fullWidth variant="text" onClick={() => cleanFilter()}>
              Limpar filtros
            </Button>
          </Grid2>
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default StudentFilters;
